import { ChartOptions } from 'chart.js';
import { enUS } from 'date-fns/locale';

interface ChartConfigOptions {
  sensorName: string;
  selectedRangeHours: number;
  onZoomChange: (zoomed: boolean) => void;
  showHumidity: boolean;
  showBattery: boolean;
  showTemperature?: boolean;
  showRSSI?: boolean;
  showSNR?: boolean;
}


export const getChartOptions = ({
  sensorName,
  selectedRangeHours,
  onZoomChange,
  showHumidity,
  showBattery,
  showTemperature = true,
  showRSSI = false,
  showSNR = false
}: ChartConfigOptions): ChartOptions<'line'> => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 0
  },
  layout: {
    padding: {
      top: window.innerWidth < 768 ? 10 : 5,
      right: window.innerWidth < 768 ? 15 : 20,
      bottom: window.innerWidth < 768 ? 0 : 5,
      left: window.innerWidth < 768 ? 0 : 10
    }
  },
  plugins: {
    zoom: {
      pan: {
        enabled: true,
        mode: 'x',
        modifierKey: window.innerWidth >= 768 ? 'ctrl' : undefined,
        threshold: window.innerWidth < 768 ? 5 : 10,
      },
      zoom: {
        pinch: {
          enabled: true
        },
        wheel: {
          enabled: true,
          modifierKey: window.innerWidth >= 768 ? 'ctrl' : undefined,
        },
        drag: {
          // On mobile drag conflicts with pan (both use single-finger touch), so disable it.
          // Mobile users zoom with pinch; desktop users drag to select a zoom region.
          enabled: window.innerWidth >= 768,
          backgroundColor: 'rgba(99, 102, 241, 0.2)',
          borderColor: 'rgb(99, 102, 241)',
          borderWidth: 1,
          threshold: 10
        },
        mode: 'x'
      },
    },
    legend: {
      position: window.innerWidth < 768 ? 'bottom' : 'top',
      align: window.innerWidth < 768 ? 'start' : 'center',
      labels: {
        boxWidth: window.innerWidth < 768 ? 8 : 40,
        padding: window.innerWidth < 768 ? 8 : 20,
        font: {
          size: window.innerWidth < 768 ? 10 : 12
        },
        filter: (item: any) => !item.text.startsWith('_'),
      }
    },
    title: {
      display: true,
      text: (() => {
        if (window.innerWidth < 768) return sensorName;
        const parts = [
          showTemperature && 'Temperature',
          showHumidity && 'Humidity',
          showBattery && 'Battery',
          showRSSI && 'RSSI',
          showSNR && 'SNR'
        ].filter(Boolean) as string[];
        return parts.length ? `${sensorName} - ${parts.join(' & ')} Readings` : `${sensorName} - Readings`;
      })(),
      font: {
        size: window.innerWidth < 768 ? 12 : 16
      },
      padding: {
        top: window.innerWidth < 768 ? 5 : 10,
        bottom: window.innerWidth < 768 ? 5 : 10
      }
    }
  },
  scales: {
    x: {
      type: 'time',
      time: {
        unit: selectedRangeHours <= 1 ? 'minute'
            : selectedRangeHours <= 24 ? 'hour'
            : selectedRangeHours <= 168 ? 'day'
            : 'month',
        displayFormats: {
          minute: 'HH:mm',
          hour: 'HH:mm',
          day: 'MMM d',
          month: 'MMM yyyy'
        },
        minUnit: 'minute',
      },
      grid: {
        display: false
      },
      border: {
        display: false
      },
      title: {
        display: false
      },
      ticks: {
        maxRotation: 0,
        font: {
          size: window.innerWidth < 768 ? 9 : 12
        },
        maxTicksLimit: window.innerWidth < 768 ? 6 : 12,
        padding: window.innerWidth < 768 ? 0 : 5
      }
    },
    y: {
      type: 'linear',
      display: true,
      position: 'left',
      grid: {
        color: 'rgba(0, 0, 0, 0.1)'
      },
      border: {
        display: false
      },
      title: {
        display: false
      },
      ticks: {
        font: {
          size: window.innerWidth < 768 ? 9 : 12
        },
        padding: window.innerWidth < 768 ? 0 : 5,
        count: window.innerWidth < 768 ? 5 : undefined
      }
    },
    y1: {
      type: 'linear',
      display: showHumidity,
      position: 'right',
      grid: {
        drawOnChartArea: false
      },
      border: {
        display: false
      },
      title: {
        display: false
      },
      ticks: {
        font: {
          size: window.innerWidth < 768 ? 9 : 12
        },
        padding: window.innerWidth < 768 ? 0 : 5,
        count: window.innerWidth < 768 ? 5 : undefined
      }
    },
    y2: {
      type: 'linear',
      display: showBattery,
      position: 'right',
      grid: {
        drawOnChartArea: false
      },
      border: {
        display: false
      },
      title: {
        display: false
      },
      ticks: {
        font: {
          size: window.innerWidth < 768 ? 9 : 12
        },
        padding: window.innerWidth < 768 ? 0 : 5,
        count: window.innerWidth < 768 ? 5 : undefined
      }
    },
    y3: {
      type: 'linear',
      display: showRSSI,
      position: 'right',
      grid: {
        drawOnChartArea: false
      },
      border: {
        display: false
      },
      title: {
        display: false
      },
      ticks: {
        font: {
          size: window.innerWidth < 768 ? 9 : 12
        },
        padding: window.innerWidth < 768 ? 0 : 5,
        count: window.innerWidth < 768 ? 5 : undefined
      }
    },
    y4: {
      type: 'linear',
      display: showSNR,
      position: 'right',
      grid: {
        drawOnChartArea: false
      },
      border: {
        display: false
      },
      title: {
        display: false
      },
      ticks: {
        font: {
          size: window.innerWidth < 768 ? 9 : 12
        },
        padding: window.innerWidth < 768 ? 0 : 5,
        count: window.innerWidth < 768 ? 5 : undefined
      }
    }
  }
});